import React, { useState } from 'react';
import { getFirestore, collection, addDoc, getDocs, writeBatch, doc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { sendNotification } from '../../services/oneSignalService';
import imageCompression from 'browser-image-compression';
import { Timestamp } from 'firebase/firestore';

// Composants UI
import Modal from '../ui/Modal/Modal';
import Button from '../ui/Button/Button';
import Input from '../ui/Input/Input';
import TextArea from '../ui/TextArea/TextArea';
import Select from '../ui/Select/Select';
import './CreateNewsForm.css';

const CreateNewsForm = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    urlRedirection: '',
    categorie: '',
    notificationTitle: '',
    notificationDescription: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sendNotif, setSendNotif] = useState(true);

  // Catégories prédéfinies uniquement
  const categories = [
    'E-liquide',
    'E-cigarette',
    'CBD',
    'Accessoire'
  ];

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        // Options de compression
        const options = {
          maxSizeMB: 0.5,          // Taille max : 500KB
          maxWidthOrHeight: 1200,   // Dimension max : 1200px
          useWebWorker: true,       // Utiliser un Web Worker pour ne pas bloquer l'UI
          preserveExif: false,      // Supprimer les métadonnées pour réduire la taille
          quality: 0.7              // Qualité de compression à 70%
        };

        // Compression de l'image
        const compressedFile = await imageCompression(file, options);
        
        // Mise à jour du state avec l'image compressée
        setImageFile(compressedFile);
        
        // Création de l'URL pour la prévisualisation
        const previewUrl = URL.createObjectURL(compressedFile);
        setImagePreview(previewUrl);
      } catch (error) {
        console.error('Erreur lors de la compression:', error);
        alert('Erreur lors du traitement de l\'image');
      }
    }
  };

  const handleDescriptionChange = (e) => {
    const { value } = e.target;
    if (value.length <= 205) {
      setFormData(prev => ({
        ...prev,
        description: value
      }));
    }
  };

  const handleTitleChange = (e) => {
    const value = e.target.value;
    if (value.length <= 55) {
      setFormData({...formData, titre: value});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Upload image et création dans Firestore
      let imageUrl = '';
      if (imageFile) {
        const storage = getStorage();
        const timestamp = Date.now();
        const storageRef = ref(storage, `nouveautes/${timestamp}_${imageFile.name}`);
        
        // Ajout des métadonnées pour le cache
        const metadata = {
          cacheControl: 'max-age=604800', // 7 jours en secondes
          contentType: imageFile.type || 'image/jpeg'
        };
        
        await uploadBytes(storageRef, imageFile, metadata);
        imageUrl = await getDownloadURL(storageRef);
      }

      const db = getFirestore();
      const newsRef = collection(db, 'nouveautes');
      
      // Récupérer tous les documents existants
      const snapshot = await getDocs(newsRef);
      const batch = writeBatch(db);

      // Incrémenter l'ordre de tous les éléments existants
      snapshot.docs.forEach((doc) => {
        const docRef = doc.ref;
        batch.update(docRef, { order: doc.data().order + 1 });
      });

      // Créer la nouvelle news avec order: 0
      const newNewsRef = doc(collection(db, 'nouveautes'));
      await batch.set(newNewsRef, {
        titre: formData.titre,
        description: formData.description,
        categorie: formData.categorie,
        imageUrl,
        urlRedirection: formData.urlRedirection,
        dateCreation: new Date().toISOString(),
        timestamp: Timestamp.fromDate(new Date()),
        order: 0,
      });

      // Exécuter toutes les mises à jour en une seule fois
      await batch.commit();

      // Envoyer la notification uniquement si sendNotif est true
      if (sendNotif) {
        // Préparer la date pour OneSignal si elle est définie

        await sendNotification({
          title: formData.notificationTitle,
          description: formData.notificationDescription,
          imageUrl,
          url: `https://www.vaposmoketoday.fr/news`,
          type: 'news',
        });
      }

      onSuccess();
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      alert('Une erreur est survenue lors de la création');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal 
      title="Nouvelle publication"
      onClose={onClose}
      className="news-form"
    >
      <div className="modal-sections">
        <form onSubmit={handleSubmit} className="form-section">
          <div className="form-group">
            <Input
              label="Titre"
              name="titre"
              value={formData.titre}
              onChange={handleTitleChange}
              placeholder="Titre de la nouveauté"
              required
            />
            <div className="character-count">
              {formData.titre.length}/55 caractères
            </div>
            <p className="field-info">
              Ce titre sera affiché dans l'application mobile.
            </p>
          </div>

          <div className="form-group">
            <TextArea
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleDescriptionChange}
              placeholder="Entrez la description (max 205 caractères)"
              required
            />
            <div className="character-count">
              {formData.description.length}/205 caractères
            </div>
            <p className="field-info">
              Description de la nouveauté qui sera affichée dans l'application mobile.
            </p>
          </div>

          <div className="form-group">
            <Input
              type="url"
              label="URL de redirection"
              name="urlRedirection"
              value={formData.urlRedirection}
              onChange={(e) => setFormData({...formData, urlRedirection: e.target.value})}
              placeholder="https://..."
            />
            <p className="field-info">
              URL complète de la nouveauté (https://...).
            </p>
          </div>

          <div className="form-group">
            <Select
              label="Catégorie"
              name="categorie"
              value={formData.categorie}
              onChange={(e) => setFormData({...formData, categorie: e.target.value})}
              options={categories.map(cat => ({ value: cat, label: cat }))}
              required
            />
            <p className="field-info">
              La catégorie permet d'organiser les publications et d'aider les utilisateurs à trouver plus facilement le contenu qui les intéresse.
            </p>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={sendNotif}
                onChange={(e) => setSendNotif(e.target.checked)}
              />
              <span>Envoyer une notification push</span>
            </label>
            <p className="field-info">
              Si cette option est décochée, la publication sera visible dans l'application mais aucune notification ne sera envoyée aux utilisateurs.
            </p>
          </div>

          {sendNotif && (
            <>
              <div className="form-group">
                <Input
                  label="Titre de la notification"
                  name="notificationTitle"
                  value={formData.notificationTitle}
                  onChange={(e) => {
                    if (e.target.value.length <= 25) {
                      setFormData({...formData, notificationTitle: e.target.value});
                    }
                  }}
                  placeholder="Titre notification"
                  required={sendNotif}
                />
                <div className="character-count">
                  {formData.notificationTitle.length}/25 caractères
                </div>
                <p className="field-info">
                  Titre de la notification que les utilisateurs verront.
                </p>
              </div>

              <div className="form-group">
                <TextArea
                  label="Description de la notification"
                  name="notificationDescription"
                  value={formData.notificationDescription}
                  onChange={(e) => {
                    if (e.target.value.length <= 65) {
                      setFormData({...formData, notificationDescription: e.target.value});
                    }
                  }}
                  placeholder="Description notification"
                  required={sendNotif}
                />
                <div className="character-count">
                  {formData.notificationDescription.length}/65 caractères
                </div>
                <p className="field-info">
                  Description de la notification que les utilisateurs verront.
                </p>
              </div>
            </>
          )}

          <div className="form-actions">
            <Button 
              variant="secondary"
              onClick={onClose}
              disabled={isLoading}
              className="cancel-btn"
              type="button"
            >
              Annuler
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={isLoading}
              className="submit-btn"
            >
              Créer la publication
            </Button>
          </div>
        </form>

        <div className="image-section">
          <div className="form-group">
            <label>Image</label>
            <div className="image-upload-zone">
              {!imagePreview ? (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="image-input"
                    id="image-input"
                    required
                  />
                  <label htmlFor="image-input" className="upload-label">
                    <i className="fas fa-cloud-upload-alt"></i>
                    <span>Glisser une image ou cliquer pour sélectionner</span>
                  </label>
                </>
              ) : (
                <div className="image-preview-container">
                  <img src={imagePreview} alt="Prévisualisation" />
                  <button 
                    type="button" 
                    className="remove-image"
                    onClick={() => {
                      setImageFile(null);
                      setImagePreview(null);
                    }}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreateNewsForm; 